import { referenceDisplay } from '@seen/core/display'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import type { DocumentReviewTask } from '@seen/core/models/tasks/standard/documentReviewTask'
import { Button, Separator, TypographyOverline } from '@seen/ui'
import { IconFile, IconX } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { Activity } from '#src/components/activity/Activity.tsx'
import { AttachmentDisplay } from '#src/components/attachments/AttachmentDisplay.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { DocumentReviewToolbar } from '#src/components/document-reference/DocumentReviewToolbar.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useRead } from '#src/hooks/useRead.ts'
import { TaskActivity } from '#src/pages/task/TaskActivity.tsx'

interface DocumentInboxDetailProps {
  task: DocumentReviewTask
  onClose: () => void
  onUpdate: () => void
  onComplete: () => void
}

export function DocumentInboxDetail({
  task,
  onClose,
  onUpdate,
  onComplete,
}: DocumentInboxDetailProps): ReactNode {
  const { data: docRef } = useRead(ExtendedDocumentReference, task.document)

  if (!docRef) {
    return (
      <div className="flex h-full flex-col border-l border-border bg-background">
        <div className="flex flex-1 items-center justify-center">
          Loading...
        </div>
      </div>
    )
  }

  const attachments = docRef.attachments ?? []

  return (
    <div className="flex h-full flex-col border-l border-border bg-background">
      <div className="flex items-center justify-between gap-3 border-b border-border p-4">
        <div className="flex min-w-0 items-center gap-3">
          <IconFile size={24} strokeWidth={1} className="shrink-0" />
          <div className="min-w-0">
            <div className="truncate font-medium">
              {docRef.title || 'Untitled Document'}
            </div>
            <div className="text-xs text-muted-foreground">
              Uploaded {docRef.timeSinceUploaded} ago
              {referenceDisplay(docRef.author) &&
                ` by ${referenceDisplay(docRef.author)}`}
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="shrink-0"
        >
          <IconX className="h-5 w-5" />
        </Button>
      </div>

      <DocumentReviewToolbar
        docRef={docRef}
        task={task}
        onUpdate={onUpdate}
        onComplete={onComplete}
      />

      <ScrollArea className="flex-1" scrollRestorationId="fax-document-detail">
        <div className="space-y-6 p-4">
          {attachments.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {attachments.map((attachment, index) => (
                <div
                  key={attachment.id}
                  className={
                    index === attachments.length - 1 &&
                    attachments.length % 2 === 1
                      ? 'col-span-2'
                      : ''
                  }
                >
                  <AttachmentDisplay value={attachment} />
                </div>
              ))}
            </div>
          )}

          {docRef.description && (
            <div>
              <TypographyOverline>Description</TypographyOverline>
              <RichTextEditor
                defaultValue={docRef.description}
                variant="unstyled"
                editable={false}
              />
            </div>
          )}

          {docRef.aiSummary && (
            <div>
              <TypographyOverline>AI Summary</TypographyOverline>
              <RichTextEditor
                defaultValue={docRef.aiSummary}
                variant="unstyled"
                editable={false}
              />
            </div>
          )}

          <Separator />
          <Activity resource={task} subject={task.participant}>
            <TaskActivity task={task} />
          </Activity>
        </div>
      </ScrollArea>
    </div>
  )
}
